import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ProgressBar } from '../../components/ProgressBar';
import { 
  Users, 
  Ticket, 
  Trophy, 
  DollarSign, 
  FileCheck,
  CreditCard,
  ArrowLeft,
  RefreshCw,
  Play,
  TrendingUp,
  Clock,
  CheckCircle,
  BarChart3,
  Zap,
  QrCode,
  Bell,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);

  const fetchDashboard = async () => {
    try {
      const [dashboardData, paymentsData] = await Promise.all([
        api.admin.getDashboard(),
        api.admin.getPendingQRPayments().catch(() => [])
      ]);
      setDashboard(dashboardData);
      setPendingPayments(paymentsData);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Auto-refresh every 15 seconds
    const interval = setInterval(fetchDashboard, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleExecuteDraw = async () => {
    if (!dashboard?.active_raffle?.id) return;
    
    if (!window.confirm('¿Ejecutar el sorteo ahora? Esta acción no se puede deshacer.')) {
      return;
    }
    
    setExecuting(true);
    try {
      const result = await api.admin.executeDraw(dashboard.active_raffle.id);
      toast.success(`🎉 ¡Sorteo ejecutado! Ganador: ${result.winning_number}`);
      if (result.inverted_number) {
        toast.info(`Número invertido: ${result.inverted_number}`);
      }
      fetchDashboard();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al ejecutar sorteo';
      toast.error(message);
    } finally {
      setExecuting(false);
    }
  };

  const handleResetAllData = async () => {
    if (!window.confirm('⚠️ PELIGRO: Esto eliminará TODOS los datos:\n\n• Todos los usuarios (excepto admin)\n• Todos los sorteos\n• Todos los tickets\n• Todas las compras\n• Todos los reclamos\n\n¿Estás SEGURO?')) {
      return;
    }
    if (!window.confirm('⚠️ ÚLTIMA CONFIRMACIÓN:\n\nSe creará un nuevo sorteo STP0001 y se perderán TODOS los datos anteriores.\n\n¿CONFIRMAR RESET TOTAL?')) {
      return;
    }
    
    try {
      const result = await api.admin.resetAllData();
      toast.success(`✅ Base de datos reiniciada. Nuevo sorteo: ${result.new_raffle}`);
      fetchDashboard();
    } catch (error) {
      toast.error('Error al reiniciar datos');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const brebPending = pendingPayments.filter(p => p.payment_method === 'nequi').length;
  const bancolombiaPending = pendingPayments.filter(p => p.payment_method === 'bancolombia').length;
  const totalPendingAmount = pendingPayments.reduce((sum, p) => sum + (p.exact_amount || 0), 0);

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      </div>
    );
  }

  const progressPercentage = dashboard?.active_raffle 
    ? (dashboard.active_raffle.sold_tickets / dashboard.active_raffle.total_tickets) * 100 
    : 0;

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="heading-2">Panel Admin</h1>
            <p className="text-xs text-muted-foreground">tuprima.com</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="destructive" 
            size="sm"
            onClick={handleResetAllData}
            className="text-xs"
            data-testid="reset-all-btn"
          >
            Reset Total
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchDashboard}
            className="rounded-full"
            data-testid="refresh-dashboard"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Pending Payments Alert */}
      {pendingPayments.length > 0 && (
        <Link to="/admin/payments">
          <Card className="card-base bg-gradient-to-r from-amber-500 to-orange-500 text-white border-none" data-testid="pending-alert">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <QrCode className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{pendingPayments.length} pagos pendientes</p>
                    <p className="text-amber-100 text-sm">
                      {formatCurrency(totalPendingAmount)} por aprobar
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="bg-emerald-600 px-2 py-1 rounded-full text-xs font-medium">
                    Bre-B: {brebPending}
                  </span>
                  <span className="bg-yellow-600 px-2 py-1 rounded-full text-xs font-medium">
                    Banco: {bancolombiaPending}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="card-base" data-testid="stat-users">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.total_participants || dashboard?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground">Participantes</p>
                {dashboard?.total_admins > 0 && (
                  <p className="text-xs text-slate-400">+{dashboard.total_admins} admins</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-lg font-bold">{formatCurrency(dashboard?.total_revenue || 0)}</p>
                <p className="text-xs text-muted-foreground">Ingresos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-raffles">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.total_raffles || 0}</p>
                <p className="text-xs text-muted-foreground">Sorteos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-completed">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.completed_raffles || 0}</p>
                <p className="text-xs text-muted-foreground">Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Raffle Card */}
      {dashboard?.active_raffle && (
        <Card className="card-base border-2 border-primary/30" data-testid="active-raffle-admin">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Sorteo Activo
              </CardTitle>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                dashboard.active_raffle.status === 'countdown' 
                  ? 'bg-accent/10 text-accent animate-pulse' 
                  : dashboard.active_raffle.status === 'waiting'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-success/10 text-success'
              }`}>
                {dashboard.active_raffle.status === 'countdown' ? '¡Sorteando!' : 
                 dashboard.active_raffle.status === 'waiting' ? 'En espera' : 'Activo'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="font-semibold text-lg">{dashboard.active_raffle.name}</h3>
            
            {/* Progress */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Vendidos</span>
                <span className="font-bold">
                  {dashboard.active_raffle.sold_tickets} / {dashboard.active_raffle.total_tickets}
                </span>
              </div>
              <ProgressBar value={progressPercentage} showLabel={false} />
              <p className="text-right text-xs text-muted-foreground mt-1">
                {progressPercentage.toFixed(1)}%
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-emerald-50 rounded-xl p-3">
                <p className="text-xs text-emerald-600">Premio Mayor</p>
                <p className="font-bold text-emerald-700">{formatCurrency(1000000)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-blue-600">Invertido</p>
                <p className="font-bold text-blue-700">{formatCurrency(200000)}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-3">
                <p className="text-xs text-purple-600">Top Buyer</p>
                <p className="font-bold text-purple-700">{formatCurrency(100000)}</p>
              </div>
            </div>

            {/* Revenue for this raffle */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium">Recaudado</span>
                </div>
                <span className="text-lg font-bold text-emerald-600">
                  {formatCurrency(dashboard.active_raffle.sold_tickets * 2500)}
                </span>
              </div>
            </div>

            {/* Execute Draw Button */}
            {dashboard.active_raffle.sold_tickets > 0 && dashboard.active_raffle.status === 'active' && (
              <Button
                onClick={handleExecuteDraw}
                disabled={executing}
                className="w-full bg-gradient-to-r from-accent to-orange-500 hover:from-accent/90 hover:to-orange-500/90 rounded-full h-12 text-lg"
                data-testid="execute-draw-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                {executing ? 'Ejecutando...' : 'Ejecutar Sorteo Ahora'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analytics Section */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Resumen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Tickets vendidos (total)</p>
              <p className="text-xl font-bold">{dashboard?.total_tickets_sold || 0}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Premios entregados</p>
              <p className="text-xl font-bold">{formatCurrency((dashboard?.completed_raffles || 0) * 1300000)}</p>
            </div>
          </div>
          
          <div className="bg-emerald-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600">Margen estimado</p>
                <p className="text-sm text-muted-foreground">(Ingresos - Premios)</p>
              </div>
              <p className="text-xl font-bold text-emerald-700">
                {formatCurrency((dashboard?.total_revenue || 0) - ((dashboard?.completed_raffles || 0) * 1300000))}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="space-y-3">
        <h2 className="heading-4">Acciones Rápidas</h2>
        
        <Link to="/admin/payments">
          <Card className="card-base card-interactive border-amber-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium">Pagos QR Pendientes</p>
                  <p className="text-xs text-muted-foreground">
                    {pendingPayments.length} por aprobar
                  </p>
                </div>
              </div>
              {pendingPayments.length > 0 && (
                <span className="bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                  {pendingPayments.length}
                </span>
              )}
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/raffles">
          <Card className="card-base card-interactive">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Gestionar Sorteos</p>
                  <p className="text-xs text-muted-foreground">Crear, ver y administrar</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/claims">
          <Card className="card-base card-interactive">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="font-medium">Reclamos de Premio</p>
                  <p className="text-xs text-muted-foreground">
                    {dashboard?.pending_claims || 0} pendientes
                  </p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/referrals">
          <Card className="card-base card-interactive">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium">Ranking Referidos</p>
                  <p className="text-xs text-muted-foreground">Premio mensual $1M</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/tickets">
          <Card className="card-base card-interactive border-blue-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Gestión de Tickets</p>
                  <p className="text-xs text-muted-foreground">Validar, marcar premiados y pagados</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/social-proof">
          <Card className="card-base card-interactive border-amber-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium">Prueba Social</p>
                  <p className="text-xs text-muted-foreground">Globos de premios pagados</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/free-raffle">
          <Card className="card-base card-interactive border-purple-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium">Sorteo Gratuito</p>
                  <p className="text-xs text-muted-foreground">Activar/desactivar y gestionar participantes</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/participants">
          <Card className="card-base card-interactive border-emerald-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="font-medium">Vista de Participantes</p>
                  <p className="text-xs text-muted-foreground">Historial, filtros y exportar CSV</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/platform-stats">
          <Card className="card-base card-interactive border-blue-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Estadísticas Públicas</p>
                  <p className="text-xs text-muted-foreground">Métricas del bloque de confianza</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
